<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import store from '@/lib/store';
import { useAiSearch } from '@/assets/modules/composables/aiSearch.js';

/**
 * Natural-language entity search (#257).
 *
 * The user asks a question in plain English; the server has Claude compose a
 * structured filter, runs it through the permission-scoped read path, and
 * returns the rows plus a sentence describing the filter that produced them.
 *
 * That sentence is the reason this view is trustworthy, and it is not
 * decoration. A search that quietly omits matching rows — because the model
 * picked the wrong field, or read "over 50,000" as a text match — renders
 * identically to a correct one. Showing the applied filter is what makes the
 * difference visible, and it is why the filter stays on screen when the result
 * set is empty: "no results" alone reads as "there is no such data", where "no
 * results, and here is what I searched for" reads as "rephrase that".
 *
 * No display text about the *data* is composed here. Column titles and the
 * filter sentence are built server-side from `entity.json`; the client has no
 * copy of the schema and any label it invented would drift from it. The heading
 * and the disclaimer are the exceptions: they describe the feature, not any
 * record, so there is nothing for them to drift from.
 */

const { readSearchResponse, readRowRouteName } = useAiSearch();

const router = useRouter();

const question = ref('');
const result = ref(null);
const loading = ref(false);
const errorMessage = ref('');

async function runSearch() {
    const asked = question.value.trim();
    if (asked.length < 1) return;

    loading.value = true;
    errorMessage.value = '';
    // Cleared so a previous result is never shown beside a new question — the
    // pairing of question and filter is the whole point of the view.
    result.value = null;

    const response = await store.dispatch('aiSearch', asked);

    if (response.errorMessage) {
        errorMessage.value = response.errorMessage;
    } else {
        result.value = readSearchResponse(response.data);
    }

    loading.value = false;
}

/**
 * The read route for whatever entity type the search ran against, or '' if that
 * type has no read page. Looked up from the router's own table so it cannot
 * disagree with the routes that exist, and recomputed per result because a
 * single search view produces rows of a different entity type each time.
 */
const rowRouteName = computed(() => readRowRouteName(result.value?.entityType ?? '', router.getRoutes()));

/**
 * Rows are only clickable when there is somewhere to go. Offering the pointer
 * cursor on an entity type with no read page promises navigation that then does
 * nothing, which reads as a broken page rather than an absent feature.
 */
const rowsAreLinked = computed(() => rowRouteName.value !== '');

function openRow(_event, { item }) {
    if (!rowsAreLinked.value || !item?._id) return;

    // `_id` is the record's own id, carried on every row by the search endpoint
    // alongside the display fields. Navigating by route name rather than a built
    // path keeps the param name (`entity_id`) the router's business, not this
    // view's.
    router.push({ name: rowRouteName.value, params: { entity_id: item._id } });
}
</script>

<template>
    <v-container>
        <v-row>
            <v-col>
                <h3>AI Search</h3>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-text-field
                    v-model="question"
                    label="Ask a question about your records"
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    hide-details
                    single-line
                    :disabled="loading"
                    @keyup.enter="runSearch"
                ></v-text-field>
            </v-col>
            <v-col cols="auto" class="d-flex align-center">
                <v-btn
                    color="primary"
                    variant="outlined"
                    :loading="loading"
                    :disabled="question.trim().length < 1"
                    @click="runSearch"
                >
                    Search
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="errorMessage">
            <v-col>
                <v-alert type="warning" variant="tonal" density="compact">
                    {{ errorMessage }}
                </v-alert>
            </v-col>
        </v-row>

        <!--
            The applied filter sits above the results, always, including when
            nothing matched. It is the user's only way to tell a correct empty
            result from a misread question.
        -->
        <template v-if="result">
            <v-row v-if="result.description">
                <v-col>
                    <v-chip color="primary" variant="tonal" label>
                        {{ result.description }}
                    </v-chip>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-data-table
                        :headers="result.tableHeaders"
                        :items="result.rows"
                        item-key="_id"
                        :items-per-page="10"
                        :loading="loading"
                        loading-text="Loading..."
                        no-data-text="No records matched that search."
                        :hover="rowsAreLinked"
                        :row-props="rowsAreLinked ? { class: 'cursor-pointer' } : undefined"
                        @click:row="openRow"
                    ></v-data-table>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>
